<template>
  <FriedCard
    ref="mentionParticipantsRef"
    v-click-outside="close"
    class="mention-prospects-container"
    :style="position"
  >
    <FriedDropdownItem
      v-for="(mp, index) in mentionParticipants"
      :key="index"
      :active="index === selectedIndex"
      @click="$emit('add-mention', mp.commentParticipant)"
    >
      <template #leftIcon>@</template>
      {{ mp.fullName }}
    </FriedDropdownItem>
  </FriedCard>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { MentionParticipant } from '@getaccept/lib-shared-new/src/contextual-commenting/mentions/types/mention-participant';
import { useIntersectionObserver } from '@vueuse/core';

export default defineComponent({
  props: {
    mentionParticipants: {
      type: Array as PropType<MentionParticipant[]>,
      required: true,
    },
    verticalOffset: {
      type: Number,
      default: 10,
    },
    selectedIndex: {
      type: Number,
      required: true,
    },
  },
  emits: ['close', 'add-mention'],
  setup(props, { emit }) {
    const close = () => {
      emit('close');
    };

    const position = ref<{
      top?: string;
      bottom?: string;
    }>({ bottom: `${props.verticalOffset}px` });

    const calculatePosition = () => {
      const friedCardRect = document
        .querySelector('.mention-prospects-container')
        ?.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const topBarHeight = 3.5 * 16; // 3.5rem

      const isOutOfTopBounds = friedCardRect.top - topBarHeight < 0;
      const isOutOfBottomBounds = friedCardRect.bottom > viewportHeight;

      position.value =
        isOutOfTopBounds || isOutOfBottomBounds
          ? { top: `${props.verticalOffset}px` }
          : { bottom: `${props.verticalOffset}px` };
    };

    const mentionParticipantsRef = ref(null);

    const { stop: stopObserver } = useIntersectionObserver(
      mentionParticipantsRef,
      ([{ isIntersecting }]) => {
        if (!isIntersecting) {
          calculatePosition();
        }
      },
      { threshold: 1 }
    );

    onMounted(() => {
      calculatePosition();
    });

    watch(mentionParticipantsRef, newValue => {
      if (newValue) {
        calculatePosition();
      }
    });

    onBeforeUnmount(() => {
      stopObserver();
    });

    return {
      close,
      position,
      mentionParticipantsRef,
    };
  },
});
</script>

<style lang="scss" scoped>
.mention-prospects-container {
  position: absolute;
  background-color: white;
  z-index: 1;
  left: 0;
  width: 300px;
  max-height: 350px;
  overflow-y: auto;
  padding: var(--spacing-50);
}
</style>
